/**
 * <s-product-tabs> — PDP product information tabs.
 * Follows the WAI-ARIA tabs pattern with automatic activation.
 */
(function () {
  const SELECTORS = {
    list: '[js-product-tab-list]',
    tab: '[js-product-tab]',
    panel: '[js-product-tab-panel]',
    clamp: '[js-clamp]',
    clampToggle: '[js-clamp-toggle]',
  };

  const CLAMP_LABELS = { collapsed: 'Read more', expanded: 'Read less' };

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector(SELECTORS.list);
      this.tabs = Array.from(this.querySelectorAll(SELECTORS.tab));
      this.panels = Array.from(this.querySelectorAll(SELECTORS.panel));

      if (!this.list || this.tabs.length === 0) return;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.updateScrollState = this.updateScrollState.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.list.addEventListener('scroll', this.updateScrollState, { passive: true });
      window.addEventListener('resize', this.updateScrollState);

      const selected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(selected === -1 ? 0 : selected, { focus: false, scroll: false });
      this.updateScrollState();
      this.setupClamp();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.list) this.list.removeEventListener('scroll', this.updateScrollState);
      window.removeEventListener('resize', this.updateScrollState);
      if (this.clampToggle) this.clampToggle.removeEventListener('click', this.onClampToggle);
      window.removeEventListener('resize', this.updateClamp);
    }

    setupClamp() {
      this.clamp = this.querySelector(SELECTORS.clamp);
      this.clampToggle = this.querySelector(SELECTORS.clampToggle);
      if (!this.clamp || !this.clampToggle) return;

      this.clampExpanded = false;
      this.onClampToggle = this.onClampToggle.bind(this);
      this.updateClamp = this.updateClamp.bind(this);

      this.clampToggle.addEventListener('click', this.onClampToggle);
      window.addEventListener('resize', this.updateClamp);

      // Line heights shift when the webfont swaps in, which changes whether the
      // copy overflows eight lines — so measure again once fonts settle.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.updateClamp);
      }
      this.updateClamp();
    }

    updateClamp() {
      // Once expanded there's nothing to measure; leave the toggle as-is rather
      // than fighting the reader on resize.
      if (!this.clamp || this.clampExpanded) return;
      const overflows = this.clamp.scrollHeight - this.clamp.clientHeight > 1;
      this.clampToggle.hidden = !overflows;
    }

    onClampToggle() {
      this.clampExpanded = !this.clampExpanded;
      this.clamp.classList.toggle('is-expanded', this.clampExpanded);
      this.clampToggle.textContent = this.clampExpanded
        ? CLAMP_LABELS.expanded
        : CLAMP_LABELS.collapsed;
      this.clampToggle.setAttribute('aria-expanded', String(this.clampExpanded));
    }

    onClick(event) {
      const tab = event.target.closest(SELECTORS.tab);
      if (!tab || !this.contains(tab)) return;
      this.select(this.tabs.indexOf(tab), { focus: false });
    }

    onKeydown(event) {
      const tab = event.target.closest(SELECTORS.tab);
      if (!tab || !this.contains(tab)) return;

      const current = this.tabs.indexOf(tab);
      const last = this.tabs.length - 1;
      let next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = current === last ? 0 : current + 1;
          break;
        case 'ArrowLeft':
          next = current === 0 ? last : current - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(next);
    }

    select(index, options) {
      const settings = Object.assign({ focus: true, scroll: true }, options);
      const target = this.tabs[index];
      if (!target) return;

      this.tabs.forEach((tab, i) => {
        const isSelected = i === index;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        if (this.panels[i]) this.panels[i].hidden = !isSelected;
      });

      if (settings.focus) target.focus();
      if (settings.scroll) this.scrollTabIntoView(target);
      // A hidden panel measures as zero height, so re-check once it's shown.
      this.updateClamp();
    }

    /**
     * Centers the tab within its own strip. Avoids `scrollIntoView`, which would
     * also scroll the page and yank the product image out of view.
     */
    scrollTabIntoView(tab) {
      if (!this.isOverflowing()) return;
      const listRect = this.list.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const delta = tabRect.left - listRect.left - (listRect.width - tabRect.width) / 2;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.list.scrollTo({
        left: this.list.scrollLeft + delta,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    isOverflowing() {
      return this.list.scrollWidth - this.list.clientWidth > 1;
    }

    updateScrollState() {
      const overflowing = this.isOverflowing();
      this.list.classList.toggle('is-scrollable', overflowing);
      this.list.classList.toggle(
        'is-scroll-end',
        overflowing && this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 1
      );
    }
  }

  if (!customElements.get('s-product-tabs')) {
    customElements.define('s-product-tabs', ProductTabs);
  }
})();
